import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import SVG from "react-inlinesvg";
import ClearIcon from "buba-components/src/assets/icons/other/x.svg";
import ZoomIcon from "buba-components/src/assets/icons/other/zoom.svg";
import cx from "classnames";
import isEqual from "lodash/isEqual";
import { matchSorter } from "match-sorter";

import type { InputType, MetaType } from "../Input/TextInput";
import TextInput from "../Input/TextInput";
import Popover, { duration as popoverAnimationDuration } from "../Popover";
import { PopoverRenderProps } from "../Popover/Popover";
import Preloader from "../Preloader";

import { useDebounce } from "./hooks";
import styles from "./Select.scss";

interface ValueProps {
  address: string;
  city: string;
  country: string;
  crefo_id: string;
  name: string;
  state: string;
  trade_name: string;
  zip_code: string;
}

export interface SelectOption {
  value: ValueProps | string;
  labelOnSelected?: string;
  label: JSX.Element | string;
  searchString?: string;
}

export interface AdditionalSelectOption {
  value: ValueProps | string;
  labelOnSelected?: string;
  label: string;
  searchString?: string;
}

interface OptionsObject {
  [key: string]: Array<SelectOption>;
}

interface AsyncSelectProps {
  placeholder: string;
  options: OptionsObject;
  input: InputType;
  meta: MetaType;
  getAsyncOptions?: (typedValue: string) => Promise<void>;
  additionalSideSymbolStyle?: string;
  prefilledTypedValue?: string;
  selectedAsyncOption?: SelectOption;
  additionalOption?: AdditionalSelectOption;
}

const DEBOUNCE_TIME = 1000;

const AsyncSelect = ({
  prefilledTypedValue = "",
  additionalOption,
  input,
  meta,
  options = {},
  getAsyncOptions,
  placeholder,
  additionalSideSymbolStyle
}: AsyncSelectProps) => {
  const [typedValue, setTypedValue] = useState<string | ReactNode>("");
  const [isSearching, setIsSearching] = useState<boolean | undefined>(Boolean(prefilledTypedValue));
  const [isTouched, setIsTouched] = useState<boolean>(false);

  const optionRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef<boolean>(true);

  const getOptionsForTypedValue = (): Array<SelectOption> => {
    if (typeof typedValue === "string") {
      return options?.[typedValue] || [];
    }
    return [];
  };

  const isValueSelected = useCallback(
    (valueToCheck: string | ValueProps) => {
      return isEqual(valueToCheck, input.value);
    },
    [input]
  );

  const getOptionsWithAdditional = (options: Array<SelectOption>) => {
    return [...options, ...(additionalOption ? [additionalOption] : [])];
  };

  const setSearching = (searching: boolean) => setIsSearching(searching);

  const asyncOptions = async () => {
    try {
      if (getAsyncOptions && typedValue) {
        await getAsyncOptions(typedValue as string);
      }

      setSearching(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const debouncedRequest = useDebounce(asyncOptions, DEBOUNCE_TIME);

  const handleArrowNavigation = (e: KeyboardEvent<HTMLElement>) => {
    const { activeElement } = document;
    const shouldHandleKeyDown = [e.target, activeElement].every(
      el =>
        el instanceof Node &&
        optionRef &&
        optionRef.current &&
        optionRef.current.contains &&
        optionRef.current.contains(el)
    );

    if (shouldHandleKeyDown) {
      let nextFocusedElement = null;

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          nextFocusedElement = activeElement && activeElement.previousSibling;
          break;
        case "ArrowDown":
          e.preventDefault();
          nextFocusedElement = activeElement && activeElement.nextSibling;
          break;
        default:
      }
      if (nextFocusedElement && nextFocusedElement instanceof HTMLElement) {
        nextFocusedElement.focus();
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleArrowNavigation as unknown as EventListener);

    return () => {
      document.removeEventListener("keydown", handleArrowNavigation as unknown as EventListener);
    };
  }, []);

  useEffect(() => {
    // Set default value when component mounts
    if (prefilledTypedValue && textInputRef.current && isMounted.current && !input.value) {
      setTypedValue(prefilledTypedValue);
      isMounted.current = false;
      textInputRef.current.focus();
    }
  }, [input.value, prefilledTypedValue, setTypedValue]);

  useEffect(() => {
    // Load options when input value changes
    if (typedValue && prefilledTypedValue && prefilledTypedValue === typedValue && !input.value) {
      setSearching(true);
      debouncedRequest();
    }
  }, [debouncedRequest, input.value, prefilledTypedValue, typedValue]);

  useEffect(() => {
    if (isMounted.current && input.value) {
      setTypedValue(input.value.name);
      isMounted.current = false;
    }
  }, [input.value]);

  const handleInputValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsTouched(true);
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      if (typeof value === "string") {
        setTypedValue(value);

        if (!!value) {
          setSearching(true);
          debouncedRequest();
        }
      }
    }
  };

  const handleSelect = (
    option: SelectOption | AdditionalSelectOption,
    closePopover: () => void
  ) => {
    closePopover();
    input.onChange(option?.value);
    // remove it after animation is completed, to avoid flickering
    setTimeout(() => {
      setTypedValue(option.labelOnSelected || option.label);
    }, popoverAnimationDuration);
  };

  const getOptionsToRender = () => {
    const options = getOptionsForTypedValue();
    const sortedOptions = matchSorter(options, String(typedValue), {
      keys: ["label", "labelOnSelected", "searchString"]
    });

    return getOptionsWithAdditional(sortedOptions);
  };

  const renderItems = (closePopover: () => void): React.ReactNode => {
    const view = getOptionsToRender();
    if (!view.length) {
      return isTouched ? <div className={styles.notFound}>Keine Ergebnisse</div> : <div />;
    }

    return view.map((option, optionIndex: number) => (
      <button
        type="button"
        role="option"
        className={cx(styles.option, {
          [styles.optionSelected]: isValueSelected(option?.value)
        })}
        // eslint-disable-next-line react/no-array-index-key
        key={`${option.label}${optionIndex}`}
        onClick={() => {
          handleSelect(option, closePopover);
        }}
        data-closeontab={optionIndex === view.length}
        aria-label={option.label as string}
        aria-selected={isValueSelected(option?.value)}
        data-testid={`option:${input.name}-${option?.value}`}
      >
        {option.label}
      </button>
    ));
  };

  const clearValue = (closePopover: () => void) => {
    setTypedValue("");
    input.onChange("");
    closePopover();

    const textInput = textInputRef.current;
    if (textInput) {
      textInput.blur();
    }
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>, closePopover: () => void) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (optionRef.current && optionRef.current.children.length) {
        (optionRef.current.children[0] as HTMLElement).focus();
      }
    } else if (e.key === "Backspace") {
      const shouldClearValue = input?.value && !typedValue;

      if (shouldClearValue) {
        clearValue(closePopover);
      }
    }
  };

  const renderSideSymbol = (closePopover: () => void) => {
    if (input?.value) {
      return (
        <button
          type="button"
          className={styles.clear}
          onClick={() => clearValue(closePopover)}
          data-testid="clear"
        >
          <SVG src={ClearIcon} className={cx(styles.sideSymbol, additionalSideSymbolStyle)} />
        </button>
      );
    }

    return <SVG src={ZoomIcon} className={cx(styles.sideSymbol, additionalSideSymbolStyle)} />;
  };

  const renderInput = (inputProps: PopoverRenderProps) => {
    return (
      <TextInput
        input={input}
        meta={meta}
        sideSymbol={() => renderSideSymbol(inputProps.closePopover)}
        onChange={handleInputValueChange}
        value={typedValue}
        onFocus={(e: FocusEvent<HTMLInputElement>) => {
          input.onFocus(e);
          if (inputProps.onFocus) {
            inputProps.onFocus(e);
          }
        }}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          setIsTouched(false);
          input.onBlur(e);
        }}
        data-closeonshifttab
        placeholder={placeholder}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
          handleInputKeyDown(e, inputProps.closePopover)
        }
        data-testid={`selectInput:${input.name}`}
        data-name={input.name}
        innerRef={textInputRef as MutableRefObject<HTMLInputElement>}
      />
    );
  };

  const handleAsyncContent = (closePopover: () => void) => {
    if (!typedValue) {
      return null;
    }

    if (isSearching) {
      return (
        <div className={styles.loading} data-testid="spinner">
          <Preloader size="small" />
        </div>
      );
    }

    return renderItems(closePopover);
  };

  const renderContent = (closePopover: () => void) => {
    return handleAsyncContent(closePopover);
  };

  return (
    <Popover
      input={renderInput}
      content={({ closePopover }) => (
        <div
          ref={optionRef}
          className={cx(styles.optionsWrapper, {
            [styles.hideOptionsWrapper]: !typedValue
          })}
          role="listbox"
        >
          {renderContent(closePopover)}
        </div>
      )}
    />
  );
};

export default AsyncSelect;
